<script lang="ts">
	import type { IndexableKind } from "../../../core/schema/Indexable.js";
	import { getAlgoliaIndexName } from "../../../core/schema/utils/getAlgoliaIndexName.js";
	import { getGlobalSettings } from "../getGlobalSettings.js";
	import { getTenant } from "../getTenant.js";
	import { getTranslate } from "../utils/getTranslate.js";
	import translations from "./AlgoliaSearchbar.translations.json";
	import type { AlgoliaHit } from "./createAlgoliaSearcher.js";
	import { createAlgoliaSearcher, getHighlightedName } from "./createAlgoliaSearcher.js";

	export let query: string;

	const translate = getTranslate(translations);
	const { ALGOLIA_APPLICATION_ID, ALGOLIA_API_SEARCH_KEY, STORAGE_URL } = getGlobalSettings();
	const tenant = getTenant();

	const hitsPerPage = 8;
	const searcher = createAlgoliaSearcher(
		ALGOLIA_APPLICATION_ID,
		ALGOLIA_API_SEARCH_KEY,
		getAlgoliaIndexName(tenant.locale),
		hitsPerPage,
	);

	// ─── Badge config ─────────────────────────────────────────────────────────────

	type BadgeKey = "typeProduct" | "typeArticle" | "typeCaseStudy" | "typeReference";

	// Keyed by the document's `_tags` entry, so a new indexable table cannot be forgotten here.
	const TYPE_CONFIG: Record<IndexableKind, { labelKey: BadgeKey; bgClass: string }> = {
		product: { labelKey: "typeProduct", bgClass: "bg-[#FF3B30]" },
		news: { labelKey: "typeArticle", bgClass: "bg-[#007AFF]" },
		reference: { labelKey: "typeCaseStudy", bgClass: "bg-[#34C759]" },
		category: { labelKey: "typeReference", bgClass: "bg-[#30B0C7]" },
	};

	// ─── Search state ─────────────────────────────────────────────────────────────

	export let topHitName = "";

	let hits: AlgoliaHit[] = [];
	let currentPage = 0;
	let nbPages = 0;
	let isLoading = false;
	let isLoadingMore = false;
	let lastQuery = "";
	let debounceTimer: ReturnType<typeof setTimeout>;

	/* eslint-disable svelte/no-at-html-tags */
	async function fetchPage(q: string, page: number, append: boolean): Promise<void> {
		try {
			const { hits: newHits, nbPages: newNbPages } = await searcher.searchPage(q, page);
			hits = append ? [...hits, ...newHits] : newHits;
			currentPage = page;
			nbPages = newNbPages;
		} catch (err) {
			// eslint-disable-next-line no-console
			console.error("[Algolia] search failed:", err);
			if (!append) {
				hits = [];
			}
		}
	}

	async function performSearch(q: string): Promise<void> {
		const trimmed = q.trim();
		if (trimmed.length < 1) {
			hits = [];
			currentPage = 0;
			nbPages = 0;
			return;
		}
		isLoading = true;
		lastQuery = trimmed;
		await fetchPage(trimmed, 0, false);
		isLoading = false;
	}

	async function loadMore(): Promise<void> {
		if (isLoadingMore) {
			return;
		}
		isLoadingMore = true;
		try {
			await fetchPage(lastQuery, currentPage + 1, true);
		} finally {
			// eslint-disable-next-line require-atomic-updates
			isLoadingMore = false;
		}
	}

	$: {
		clearTimeout(debounceTimer);
		const q = query;
		debounceTimer = setTimeout(() => {
			void performSearch(q);
		}, 200);
	}

	const maxQueryDisplayLength = 50;
	$: displayQuery = (() => {
		const trimmed = query.trim();
		return trimmed.length > maxQueryDisplayLength ? `${trimmed.slice(0, maxQueryDisplayLength)}…` : trimmed;
	})();

	$: hasMore = currentPage < nbPages - 1;
	$: topHitName = hits[0]?.name ?? "";
</script>

{#if query.trim().length >= 1}
	{#if isLoading}
		<div class="flex items-center justify-center px-4 py-8">
			<span class="text-2xs text-center text-white/70">{translate("loading")}</span>
		</div>
	{:else if hits.length === 0}
		<div class="flex items-center justify-center px-4 py-8">
			<span class="text-2xs min-w-0 break-words text-center text-white/70"
				>{translate("noResults", { query: displayQuery })}</span
			>
		</div>
	{:else}
		<ul>
			{#each hits as item (item.objectID)}
				{@const config = TYPE_CONFIG[item._tags[0]]}
				<li class="group border-b border-white/10 last:border-b-0">
					<a
						href="/{item._data.url}"
						class="flex items-center gap-3 px-4 py-3 transition-colors duration-150 hover:bg-white/10"
					>
						<div class="size-12 flex-shrink-0">
							{#if item._data.image}
								<img src="{STORAGE_URL}/{item._data.image}" alt="" class="size-full object-cover" />
							{:else}
								<div class="flex size-full items-center justify-center">
									<svg
										class="text-ltprimary-100 size-4"
										fill="none"
										viewBox="0 0 24 24"
										stroke="currentColor"
										aria-hidden="true"
									>
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
										/>
									</svg>
								</div>
							{/if}
						</div>

						<span class="text-2xs min-w-0 flex-1 truncate font-medium text-white [&_strong]:font-bold">
							{@html getHighlightedName(item)}
						</span>

						{#if config}
							<span
								class="font-montserrat text-4xs/none h-5 flex-shrink-0 px-2 pb-[0.3125rem] pt-1 font-semibold text-white {config.bgClass}"
							>
								{translate(config.labelKey)}
							</span>
						{/if}
					</a>
				</li>
			{/each}
		</ul>

		{#if hasMore}
			<button
				type="button"
				class="text-2xs flex w-full items-center justify-center gap-2 bg-white/10 py-4 font-semibold text-white transition-colors hover:bg-white/20 disabled:opacity-60"
				on:click={loadMore}
				disabled={isLoadingMore}
			>
				{#if isLoadingMore}
					<span>{translate("loading")}</span>
				{:else}
					<span class="font-montserrat text-2xs/none font-semibold tracking-[0.02em]">{translate("loadMore")}</span>
					<svg class="h-4 w-[0.8125rem]" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
						<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
					</svg>
				{/if}
			</button>
		{/if}
	{/if}
{/if}
